import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Calendar as CalendarIcon, ShoppingBag } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-ink/50 font-serif italic" data-testid="dashboard-loading">arriving…</div>;
  }

  const bookingDates = bookings.map((b) => {
    try { return new Date(b.date + "T00:00:00"); } catch { return null; }
  }).filter(Boolean);

  const selectedDateStr = selectedDate?.toISOString().split("T")[0];
  const bookingsOnSelected = bookings.filter((b) => b.date === selectedDateStr);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative" data-testid="dashboard-page">
      <div className="absolute -top-10 right-10 w-96 h-96 bg-lavender/40 organic-blob blur-3xl -z-0 pointer-events-none" />

      <div className="relative">
        <div className="overline mb-5">Your soft room</div>
        <h1 className="font-serif text-5xl md:text-6xl text-lapis font-light leading-tight">
          Welcome back,<br /><em className="text-peach-deep">{user.name?.split(" ")[0]}</em>.
        </h1>
        <p className="mt-6 text-ink/65 max-w-xl leading-relaxed">
          Here's what you've gathered. Take whatever you need today.
        </p>
      </div>

      <div className="divider-soft my-12" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Calendar */}
        <section className="lg:col-span-5" data-testid="dashboard-calendar-section">
          <div className="overline mb-5 flex items-center gap-2"><CalendarIcon size={12} /> Your calendar</div>
          <div className="glass-strong rounded-[2rem] p-6">
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ has_booking: bookingDates }}
              modifiersClassNames={{ has_booking: "rdp-day_has_booking" }}
              data-testid="dashboard-day-picker"
            />
            <div className="divider-soft my-6" />
            <div className="overline mb-3 text-xs">
              {selectedDate ? selectedDate.toLocaleDateString("en", { weekday: "long", day: "numeric", month: "long" }) : ""}
            </div>
            {bookingsOnSelected.length === 0 ? (
              <p className="text-ink/50 font-serif italic text-sm" data-testid="dashboard-no-bookings-today">
                No sittings on this day.
              </p>
            ) : (
              <div className="space-y-2">
                {bookingsOnSelected.map((b) => (
                  <div key={b.id} className="text-sm" data-testid={`calendar-booking-${b.id}`}>
                    <div className="font-serif text-base text-ink">{b.service_title}</div>
                    <div className="text-ink/60">{b.time_slot} · <span className="text-lapis capitalize">{b.status}</span></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Bookings + Orders */}
        <div className="lg:col-span-7 space-y-10">
          <section data-testid="dashboard-bookings">
            <div className="overline mb-5">Your sittings</div>
            {bookings.length === 0 ? (
              <p className="text-ink/50 font-serif italic">No bookings yet. Sit with me when you're ready.</p>
            ) : (
              <div className="space-y-4">
                {bookings.map((b) => (
                  <div key={b.id} className="glass p-5 rounded-[1.5rem] flex justify-between items-start gap-4" data-testid={`booking-row-${b.id}`}>
                    <div>
                      <div className="font-serif text-xl text-ink">{b.service_title}</div>
                      <div className="mt-2 text-sm text-ink/60 flex flex-wrap gap-x-5">
                        <span>{b.date}</span>
                        <span>{b.time_slot}</span>
                      </div>
                    </div>
                    <span className={`text-xs uppercase tracking-widest font-semibold rounded-full px-3 py-1 ${
                      b.status === "confirmed" ? "bg-sage/30 text-sage-deep"
                      : b.status === "completed" ? "bg-lavender text-lapis"
                      : b.status === "cancelled" ? "bg-peach/40 text-peach-deep"
                      : "bg-cream text-ink/70"
                    }`}>{b.status}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section data-testid="dashboard-orders">
            <div className="overline mb-5 flex items-center gap-2"><ShoppingBag size={12} /> Your purchases</div>
            {orders.length === 0 ? (
              <p className="text-ink/50 font-serif italic">Nothing here yet — the shop is open whenever.</p>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="glass p-5 rounded-[1.5rem] flex justify-between items-center" data-testid={`order-row-${o.id}`}>
                    <div>
                      <div className="font-serif text-xl text-ink">Order</div>
                      <div className="mt-1 text-xs text-ink/50">{o.created_at?.slice(0, 10)}</div>
                    </div>
                    <div className="font-serif text-xl text-lapis">${o.amount?.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
